// =========================================================================
// Policy Status Metadata — Fuente de verdad del funnel de seguros
// =========================================================================
//
// Este archivo mapea cada valor posible de `policy_status` del modelo
// Insurance_policy del backend a:
//   - Una etiqueta amigable en español para la UI.
//   - El número de paso del funnel (orden cronológico de la compra).
//   - Una descripción de la acción del usuario hasta donde llegó.
//   - Opcionalmente, el componente del móvil (credit-apk) que disparó el cambio.
//
// El mapeo se construyó por inspección cruzada de:
//   - kredit-api-MT/models/insurance/insurance_policy.js (comentarios del schema)
//   - kredit-api-MT/services/insurance.js (asignaciones de policy_status)
//   - credit-apk/src/app/pages (vistas del flujo de compra)
//
// Si el backend incorpora un estado nuevo, agregarlo acá. Estados desconocidos
// se renderizan con fallback automático (ver `getPolicyStatusMetadata`).

export interface PolicyStatusMetadata {
  code: string;
  label: string;       // Etiqueta amigable en español
  funnelStep: number;  // Orden cronológico (1 = inicio, 10 = anulación)
  userAction: string;  // "Hasta:" que aparece en la UI
  mobileView?: string; // Componente móvil — no se muestra en UI, sirve para debug
  isTerminal?: boolean;
  isError?: boolean;
}

export const POLICY_STATUS_METADATA: Record<string, PolicyStatusMetadata> = {
  CREATED: {
    code: 'CREATED',
    label: 'Creada',
    funnelStep: 1,
    userAction: 'Seleccionó producto',
    mobileView: 'DataFormComponent'
  },
  PLAN_SELECTED: {
    code: 'PLAN_SELECTED',
    label: 'Plan seleccionado',
    funnelStep: 2,
    userAction: 'Eligió plan y frecuencia',
    mobileView: 'DataFormComponent'
  },
  DATA_CLIENT_CONFIRMED: {
    code: 'DATA_CLIENT_CONFIRMED',
    label: 'Datos confirmados',
    funnelStep: 3,
    userAction: 'Confirmó datos personales',
    mobileView: 'DataFormComponent'
  },
  LICENSE_UPLOADED: {
    code: 'LICENSE_UPLOADED',
    label: 'Carnet cargado',
    funnelStep: 4,
    userAction: 'Cargó el carnet',
    mobileView: 'AttachIdCardComponent'
  },
  VERIFIED: {
    code: 'VERIFIED',
    label: 'Vehículo verificado',
    funnelStep: 5,
    userAction: 'Verificó datos del vehículo',
    mobileView: 'VehicleDataComponent'
  },
  // Refactor Cotiza→Paga→Emite (SM 2026-08 / SC 2026-09): el backend crea el
  // invoice sin emitir para que el cliente pueda pagar contra una orden en vez
  // de contra una póliza ya emitida. Precede al pago (funnelStep 6, misma cabina
  // que ISSUED del flujo viejo — ambos son "pre-pago").
  // Ver work-plans/sc-flow-update/camino-feliz/plan-2-savevehicledata-invoice-sin-emitir.md
  INVOICE_CREATED: {
    code: 'INVOICE_CREATED',
    label: 'Orden creada',
    funnelStep: 6,
    userAction: 'Falta que el cliente pague la orden',
    mobileView: 'RcvPaymentComponent · VitalsPaymentComponent'
  },
  ISSUED: {
    code: 'ISSUED',
    label: 'Emitida',
    funnelStep: 6,
    userAction: 'Emisión confirmada por aseguradora (flujo viejo — congelado)'
    // No tiene mobileView — es un setter del backend tras integración con SM/SC
  },
  PAYMENT_RECORDED: {
    code: 'PAYMENT_RECORDED',
    label: 'Pago inicial registrado',
    funnelStep: 7,
    userAction: 'Registró el pago inicial',
    mobileView: 'RcvPaymentComponent'
  },
  // Refactor Cotiza→Paga→Emite: pisado post-emisión por complete_policy
  // (Plan 3 SC/RCV, Plan 7 SM). Distingue "emitida tras pago" del ISSUED viejo.
  // Ver work-plans/sc-flow-update/camino-feliz/plan-3-complete-policy-para-rcv.md
  ISSUED_PAID: {
    code: 'ISSUED_PAID',
    label: 'Emitida y pagada',
    funnelStep: 8,
    userAction: 'Póliza emitida tras el pago; pendiente COMPLETED (o final para suscripción)'
    // No tiene mobileView — es un setter del backend post complete_policy
  },
  COMPLETED: {
    code: 'COMPLETED',
    label: 'Pagado completo',
    funnelStep: 9,
    userAction: 'Pagó todas las cuotas',
    isTerminal: true
  },
  ANNULLED: {
    code: 'ANNULLED',
    label: 'Anulada',
    funnelStep: 10,
    userAction: 'Anulación manual o por sincronización',
    isTerminal: true
  },

  // -------------------------------------------------------------------------
  // Estados exclusivos de Venemergencia/TuDoc (insurer.code === 'VE').
  // Ver work-plans/integracion-venemergencias-tudoc/01-DECISIONES-DE-DISENO.md
  // secciones D-24 (flow tracking) y kredit-api-MT/services/venemergencias.js.
  //
  // Semántica:
  //   ISSUED  → primera afiliación OK contra TuDoc (hook post-pago CREATE).
  //   RENEWED → renovación OK (hook post-pago RENEW). Terminal por vigencia
  //             mensual del servicio TuDoc: cada mes es un ciclo cerrado.
  //   NOTIFICATION_FAILED → hook a TuDoc falló tras pago local exitoso.
  //             El cliente ya pagó pero el servicio no fue activado. Retry
  //             manual desde consola (RBAC seguros.tudoc.gestion).
  //   EXPIRED → vigencia cerrada. Cliente puede renovar.
  // -------------------------------------------------------------------------
  RENEWED: {
    code: 'RENEWED',
    label: 'Renovada',
    funnelStep: 8,
    userAction: 'Renovó la afiliación TuDoc',
    isTerminal: true
  },
  NOTIFICATION_FAILED: {
    code: 'NOTIFICATION_FAILED',
    label: 'Notificación fallida',
    funnelStep: 7,
    userAction: 'Pago OK — notificación al servicio TuDoc falló',
    isError: true
  },
  EXPIRED: {
    code: 'EXPIRED',
    label: 'Vigencia expirada',
    funnelStep: 9,
    userAction: 'Cobertura mensual expiró — puede renovar',
    isTerminal: true
  },

  // Estados legacy / edge cases que la interface seguros.interface.ts contempla
  // pero que rara vez aparecen en el dashboard. Quedan documentados acá para
  // que el fallback no se dispare con ellos.
  ACTIVE: {
    code: 'ACTIVE',
    label: 'Activa',
    funnelStep: 9,
    userAction: 'Póliza activa',
    isTerminal: true
  },
  PENDING: {
    code: 'PENDING',
    label: 'Pendiente',
    funnelStep: 0,
    userAction: 'Pendiente de procesamiento'
  },
  ERROR: {
    code: 'ERROR',
    label: 'Error',
    funnelStep: 0,
    userAction: 'Error en el flujo',
    isError: true
  }
};

// Devuelve el metadata de un código. Si no está mapeado, devuelve un fallback
// neutral que evita romper la UI cuando el backend introduce un estado nuevo.
export function getPolicyStatusMetadata(code: string): PolicyStatusMetadata {
  return POLICY_STATUS_METADATA[code] || {
    code,
    label: code,
    funnelStep: 999,
    userAction: '—'
  };
}

// Ordena una lista de items que tengan campo `status` por el funnelStep
// correspondiente. Estados desconocidos quedan al final (funnelStep 999).
export function sortByFunnelStep<T extends { status: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const stepA = getPolicyStatusMetadata(a.status).funnelStep;
    const stepB = getPolicyStatusMetadata(b.status).funnelStep;
    return stepA - stepB;
  });
}

// =========================================================================
// Breakdown — labels para sub-segmentos del bucket
// =========================================================================
//
// El backend (services/insurance_console.js#getDashboardStatusDistributionService)
// devuelve el breakdown con `label` ya resuelto, pero si por alguna razón viene
// vacío o el cliente quiere overridearlo, estas funciones son la fuente única.

export type BreakdownDimension =
  | 'by_plan_kind'
  | 'by_notification'
  | 'by_frequency'
  | 'tudoc_by_duration'
  | 'tudoc_by_validity'
  | 'tudoc_by_renewals';

const BREAKDOWN_DIMENSION_TITLES: Record<BreakdownDimension, string> = {
  by_plan_kind: 'Por tipo de pago',
  by_notification: 'Por notificación',
  by_frequency: 'Por frecuencia',
  tudoc_by_duration: 'Por duración contratada',
  tudoc_by_validity: 'Por vigencia',
  tudoc_by_renewals: 'Por renovaciones'
};

const BREAKDOWN_KEY_LABELS: Record<string, string> = {
  // by_plan_kind
  partial_credit: 'Pago inicial · cuotas pendientes',
  fully_paid_stuck: 'Pago completo (no promovido)',
  // by_notification
  notified_ok: 'Notificación OK',
  notification_error: 'Con error de notif.',
  // by_frequency
  ANUAL: 'Anual',
  SEMESTRAL: 'Semestral',
  TRIMESTRAL: 'Trimestral',
  MENSUAL: 'Mensual',
  // tudoc_by_duration (plan 22)
  '1_month': '1 mes',
  '2_months': '2 meses',
  '3_months': '3 meses',
  // tudoc_by_validity
  active: 'Vigentes',
  expired: 'Expiradas',
  // tudoc_by_renewals
  first_time: 'Primera afiliación',
  one_renewal: '1 renovación',
  two_plus_renewals: '2+ renovaciones'
};

export function getBreakdownDimensionTitle(dim: BreakdownDimension): string {
  return BREAKDOWN_DIMENSION_TITLES[dim] || dim;
}

export function getBreakdownLabel(dim: BreakdownDimension, key: string, fallback?: string): string {
  return fallback || BREAKDOWN_KEY_LABELS[key] || key;
}

// Para que la UI muestre un ícono/color semántico junto al sub-bucket.
export function getBreakdownKeyTone(key: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (key) {
    case 'notified_ok':
    case 'partial_credit':
    case 'active':
    case 'first_time':
    case 'one_renewal':
    case 'two_plus_renewals':
      return 'success';
    case 'notification_error':
    case 'expired':
      return 'warning';
    case 'fully_paid_stuck':
      return 'danger';
    default:
      return 'neutral';
  }
}

// =========================================================================
// TuDoc — Metadata de steps del flow_history[]
// =========================================================================
//
// Fuente: kredit-api-MT/services/venemergencias.js — cada `pushToFlowHistory`.
// Consumido por la sección "Trazabilidad TuDoc" del modal de detalle de póliza
// en la consola (solo visible cuando insurance.code === 'VE').
//
// Los steps que empiezan con `CLIENT_*` son emitidos por el móvil (credit-apk
// TudocService.trackStep, D-24) y solo llegan a la colección `tudoc_flow_traces`
// del backend — no al flow_history embedded. Se incluyen acá para que si el
// backend los promueve en el futuro al embedded o los expone en un endpoint
// unificado, tengan label y tono ya definido.

export interface TudocFlowStepMetadata {
  step: string;
  label: string;
  actor: 'CLIENT' | 'BACKEND' | 'HOOK';
  tone: 'info' | 'success' | 'warning' | 'danger';
  description: string;
}

export const TUDOC_FLOW_STEP_METADATA: Record<string, TudocFlowStepMetadata> = {
  POLICY_CREATED: {
    step: 'POLICY_CREATED',
    label: 'Póliza creada',
    actor: 'BACKEND',
    tone: 'info',
    description: 'Se creó el registro de póliza con status PAYMENT_RECORDED tras el submit del formulario TuDoc.'
  },
  RENEWAL_QUOTE_PREPARED: {
    step: 'RENEWAL_QUOTE_PREPARED',
    label: 'Cotización de renovación preparada',
    actor: 'BACKEND',
    tone: 'info',
    description: 'El cliente eligió meses en la pantalla de renovación. La póliza queda lista para pago.'
  },
  TUDOC_CALL_STARTED: {
    step: 'TUDOC_CALL_STARTED',
    label: 'Hook: llamada a TuDoc iniciada',
    actor: 'HOOK',
    tone: 'info',
    description: 'Post-pago local exitoso. El hook empieza a llamar al servicio TuDoc (CREATE o RENEW).'
  },
  TUDOC_NOTIFIED: {
    step: 'TUDOC_NOTIFIED',
    label: 'TuDoc notificado (primera afiliación)',
    actor: 'HOOK',
    tone: 'success',
    description: 'Hook OK. TuDoc registró al beneficiario. Estado promovido a ISSUED.'
  },
  RENEWED: {
    step: 'RENEWED',
    label: 'Renovación notificada a TuDoc',
    actor: 'HOOK',
    tone: 'success',
    description: 'Hook OK. TuDoc renovó vigencia. Estado promovido a RENEWED.'
  },
  NOTIFICATION_FAILED: {
    step: 'NOTIFICATION_FAILED',
    label: 'Falla en notificación a TuDoc',
    actor: 'HOOK',
    tone: 'danger',
    description: 'Cliente pagó pero el hook a TuDoc falló. Requiere retry manual desde consola.'
  }
};

export function getTudocFlowStepMetadata(step: string): TudocFlowStepMetadata {
  return TUDOC_FLOW_STEP_METADATA[step] || {
    step,
    label: step,
    actor: 'BACKEND',
    tone: 'info',
    description: '—'
  };
}
